import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/material/styles';
import ReplyIcon from '@mui/icons-material/Reply';
import { Grid2 as Grid, Paper, Typography, Box, Link as LinkButton, Button } from '@mui/material';
import { get } from 'lodash';

import CardHeader from '../../../DesignSystem/CardHeader';
import MenuActions from '../../../DesignSystem/MenuActions';
import { useCommentTranslation } from '../../../../Hooks/useTranslation';
import useWidth from '../../../../Hooks/useWidth';
import { cloudinaryImageUrl, CloudinaryConfig } from '../../../../Services/Cloudinary';
import InlineCommentForm from '../../../InlineCommentForm';
import AttachmentLinksList from '../../../Posts/Shared/AttachmentLinksList';
import CommentsList from '../List/List';
import useApiQuery from '../../../../Hooks/useApiQuery';
import queriesConfig from '../../../../Services/queriesConfig';

const StyledContainer = styled(Paper, {
  shouldForwardProp: (prop: any) =>
    !['isNested', 'isDeleted', 'isMobile', 'isCreatedByAdminOrFluimaster'].includes(prop),
})(({ theme, isNested, isDeleted, isMobile, isCreatedByAdminOrFluimaster }: any) => {
  if (isNested) {
    return {
      marginTop: theme.spacing(3),
      marginBottom: 0,
      marginLeft: isMobile ? 0 : theme.spacing(4),
      paddingBottom: theme.spacing(3),
      border: 'none',
    };
  }

  if (isDeleted) {
    return {
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(3),
    };
  }

  return {
    padding: theme.spacing(3),
    ...(isCreatedByAdminOrFluimaster && {
      boxShadow: `inset 4px 0px 0px ${theme.palette.secondary.main}, ${theme.shadows[1]}`,
    }),
  };
});

function Comment({
  comment,
  authorId,
  post,
  parentPost,
  isNested,
  disabled = false,
  censored = true,
  html = false,
  discussionType,
  onDestroy = () => {},
}: any) {
  const owner = get(comment, 'included.owner', {});
  const author = get(comment, 'included.author', {});
  const isMobile = useWidth('down', 'sm');
  const isCreatedByAdminOrFluimaster = comment.is_admin;
  const isAuthor = authorId === owner.id && post.authored_by !== 'a_third_party';
  const isDeleted = !!comment.deleted_at;
  const canDeleteComment = comment.permissions.can_delete_comment;
  const canReportComment = comment.permissions.can_report_comment;

  const discussionTypeFromComment = comment.discussion_type || discussionType;

  const { translateButton, content } = useCommentTranslation(comment, post.id);
  const [displayConversationThread, setDisplayConversationThread] = useState(false);

  const queryParams = {
    page: 'all',
    limit: 100,
    note_id: comment.id,
    parent_id: comment.id,
    type: discussionTypeFromComment,
  };

  const { data: subComments, refetch } = useApiQuery({
    query: queryParams.type
      ? queriesConfig.getDiscussionComments(post.id, queryParams)
      : queriesConfig.getComments(post.id, queryParams),
    options: {
      enabled: displayConversationThread,
    },
  });

  return (
    <StyledContainer
      elevation={isNested || isDeleted ? 0 : 1}
      isNested={isNested}
      isDeleted={isDeleted}
      isMobile={isMobile}
      isCreatedByAdminOrFluimaster={isCreatedByAdminOrFluimaster && !isDeleted}
      id={isCreatedByAdminOrFluimaster ? 'admin_response' : `comment_${comment.id}`}
    >
      {isDeleted ? (
        <Typography color="textSecondary">
          <FormattedMessage id="COMMENTS.THREAD.DELETED_COMMENT" />
        </Typography>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <CardHeader
              isVerified={isCreatedByAdminOrFluimaster}
              profilePictureUrl={cloudinaryImageUrl(
                author.picture?.s3_key,
                CloudinaryConfig.AVATAR_40,
                author.picture?.title,
              )}
              fullName={author.full_name}
              initials={author.initials}
              date={comment.created_at}
              placeholderType={isCreatedByAdminOrFluimaster && censored ? 'entity' : 'user'}
              alt="profile picture"
              isAuthor={isAuthor}
              action={
                <MenuActions
                  post={post}
                  key={comment.id}
                  commentId={comment.id}
                  report={canReportComment}
                  destroy={canDeleteComment}
                  discussionType={discussionTypeFromComment}
                  onDestroy={onDestroy}
                />
              }
              avatarSize={40}
              key={comment.id}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              dangerouslySetInnerHTML={{ __html: content }}
              sx={{
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                whiteSpace: html ? 'normal' : 'pre-line',
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <AttachmentLinksList attachments={comment.attachments} />
          </Box>

          <Box hidden={disabled} sx={{ mb: displayConversationThread ? 2 : 0 }}>
            <Grid container spacing={2}>
              {!isNested && (
                <Grid>
                  <LinkButton
                    variant="body1"
                    component="button"
                    onClick={() => setDisplayConversationThread(true)}
                    underline="hover"
                  >
                    <strong>
                      <FormattedMessage id="COMMENTS.THREAD.REPLY" />
                    </strong>
                  </LinkButton>
                </Grid>
              )}
              {Boolean(translateButton) && <Grid>{translateButton}</Grid>}
            </Grid>
          </Box>

          {!displayConversationThread && comment.comments_count > 0 && (
            <Box sx={{ mt: 2 }}>
              <Button
                sx={{ textTransform: 'none' }}
                onClick={() => setDisplayConversationThread(true)}
                startIcon={<ReplyIcon />}
                size="small"
              >
                <FormattedMessage
                  id="COMMENTS.THREAD.DISPLAY_CONVERSATION"
                  values={{ count: comment.comments_count }}
                />
              </Button>
            </Box>
          )}

          {displayConversationThread && (
            <>
              <Box sx={{ mt: 2 }}>
                <CommentsList
                  isNested
                  comments={subComments}
                  post={post}
                  refetch={refetch}
                  postAuthorId={authorId}
                  discussionType={discussionTypeFromComment}
                  censored={censored}
                />
              </Box>

              <Box
                hidden={disabled}
                sx={{
                  mt: 2,
                  ml: isMobile ? 0 : 4,
                }}
              >
                <InlineCommentForm
                  post={post}
                  commentId={comment.id}
                  onSubmit={refetch}
                  discussionType={discussionTypeFromComment}
                  parentPost={parentPost}
                />
              </Box>
            </>
          )}
        </>
      )}
    </StyledContainer>
  );
}

export default Comment;
